'use client';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Textarea } from '@components/Listing';
import { Modal } from '@components/Modal';
import { StarRating } from '@components/StarRating';
import { Text } from '@components/TextGroups';

import { RatingContainer } from '../styles';

interface ReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  buttons: Array<{
    label: string;
    onClick: () => void;
    variant: 'primary' | 'secondary';
    icon?: React.ReactNode;
    disabled?: boolean;
  }>;

  // Review type and target
  reviewType: 'teacher' | 'school';
  revieweeName: string; // Teacher name or school name

  // Review state
  rating: number;
  onRatingChange: (rating: number) => void;
  review: string;
  onReviewChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onReviewBlur: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
  reviewError: boolean;
}

/**
 * Reusable ReviewModal component for both teacher and school reviews
 */
export const ReviewModal: React.FC<ReviewModalProps> = ({
  isOpen,
  onClose,
  buttons,
  reviewType,
  revieweeName,
  rating,
  onRatingChange,
  review,
  onReviewChange,
  onReviewBlur,
  reviewError,
}) => {
  const { t } = useTranslation('common');

  // Get the appropriate translation key based on review type
  const getReviewText = () => {
    if (reviewType === 'teacher') {
      return t('schoolShifts.reviewForTeacher', { teacherName: revieweeName });
    } else {
      return t('teacherShifts.reviewForSchool', { schoolName: revieweeName });
    }
  };

  return (
    <Modal fullScreen title={t('review')} buttons={buttons} isOpen={isOpen} onClose={onClose}>
      <RatingContainer>
        <Text>{getReviewText()}</Text>
        <StarRating rating={rating} onRatingChange={onRatingChange} />
        <Textarea
          id="review"
          placeholder={t('writeReview')}
          name="review"
          label={t('explanation')}
          hasError={reviewError}
          required
          value={review}
          onChange={onReviewChange}
          onBlur={onReviewBlur}
          disableFullScreen={true}
        />
      </RatingContainer>
    </Modal>
  );
};
